"use client";

import React from "react";
import { ShieldCheck, Lock, Loader, Building2, User, Mail, Phone, FileText, CloudUpload, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ApplyForProfessionalAccount() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="222412" id="consultation-booking" className="dark bg-background text-foreground relative py-24 overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/5 blur-[120px]" />
        <div className="absolute top-[40%] left-[60%] w-[20%] h-[20%] rounded-lg bg-accent/5 blur-[80px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-serif font-bold mb-4 tracking-tight text-foreground">
            Bespoke Floral Consultations
          </h2>
          <p className="text-lg text-muted-foreground font-default max-w-2xl mx-auto">
            Collaborate with our master horticulturists to design breathtaking arrangements for your weddings, private events, or seasonal home transformations.
          </p>
        </div>

        <div className={`max-w-4xl mx-auto transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <FormProvider formKey="consultation_booking_event_services" formId="consultation_booking_event_services" sectionName="ApplyForProfessionalAccount" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          organization_name: "",
          position: "",
          license_number: "",
          license_document: "",
          additional_info: ""
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          organization_name: {
            required: true
          },
          position: {
            required: true
          },
          license_number: {
            required: true
          },
          license_document: {
            required: true
          }
        }} submitText="Request Your Consultation" successMessage="Your inquiry has been received. Our floral designers will reach out within 48 hours to begin your botanical journey.">
            <ProfessionalApplicationForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ProfessionalApplicationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="bg-card/40 backdrop-blur-xl border-border shadow-xl shadow-primary/5 relative overflow-hidden rounded-md">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-accent" />
        <CardContent className="pt-12 pb-12 flex flex-col items-center text-center space-y-6">
          <div className="size-20 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <CircleCheck className="size-10 text-primary" />
          </div>
          <CardTitle className="text-2xl md:text-3xl font-serif">Inquiry Received</CardTitle>
          <CardDescription className="text-base md:text-lg max-w-md mx-auto text-muted-foreground font-default">
            {successMessage}
          </CardDescription>
          <Button onClick={resetForm} variant="outline" className="mt-8 rounded-md border-primary text-primary hover:bg-primary/10">
            Send Another Inquiry
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="bg-card/40 backdrop-blur-xl border-border shadow-2xl shadow-primary/5 relative rounded-md">
      {}
      <div className="absolute -top-4 -right-2 md:-right-4 bg-accent text-accent-foreground px-4 py-1.5 rounded-md text-xs md:text-sm font-semibold flex items-center gap-1.5 shadow-md z-10 border border-border/20">
        <ShieldCheck className="size-4" />
        Artisanal Service
      </div>

      <CardHeader className="pb-8 border-b border-border/30">
        <CardTitle className="text-2xl font-serif flex items-center gap-2 text-foreground">
          <Building2 className="size-6 text-primary" />
          Event & Wedding Inquiry
        </CardTitle>
        <CardDescription className="text-base font-default">
          Share your vision with us. We specialize in garden-grown aesthetics and sustainable floral artistry.
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-8">
        <Form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {}
            <FormField control={control} name="full_name" rules={{
            required: "Full name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default text-foreground">
                    <User className="size-4 text-muted-foreground" />
                    Contact Name
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Evelyn Rose" {...field} className="bg-background/30 border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                  </FormControl>
                  <FormDescription className="text-xs">Your preferred name for correspondence.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default text-foreground">
                    <Mail className="size-4 text-muted-foreground" />
                    Email Address
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="evelyn@example.com" {...field} className="bg-background/30 border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="phone" rules={{
            required: "Phone number is required",
            pattern: {
              value: /^\+?[0-9\s\-\(\)]+$/,
              message: "Please enter a valid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default text-foreground">
                    <Phone className="size-4 text-muted-foreground" />
                    Phone Number
                  </FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="bg-background/30 border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="organization_name" rules={{
            required: "Event type is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default text-foreground">
                    <Building2 className="size-4 text-muted-foreground" />
                    Event Type
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Wedding, Gala, or Private Dinner" {...field} className="bg-background/30 border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="position" rules={{
            required: "Event date is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default text-foreground">Event Date</FormLabel>
                  <FormControl>
                    <Input placeholder="June 12, 2024" {...field} className="bg-background/30 border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="license_number" rules={{
            required: "Venue location is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2 font-default text-foreground">
                    <FileText className="size-4 text-muted-foreground" />
                    Venue Location
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="The Glass House, NY" {...field} className="bg-background/30 border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary transition-colors" />
                  </FormControl>
                  <FormDescription className="text-xs">Where will your celebration take place?</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          {}
          <FormField control={control} name="license_document" rules={{
          required: "Mood board is required"
        }} render={({
          field: {
            value,
            onChange,
            ...fieldProps
          }
        }) => <FormItem className="bg-background/20 p-6 rounded-md border border-dashed border-border/40">
                <FormLabel className="flex items-center gap-2 text-base font-default text-foreground">
                  <CloudUpload className="size-5 text-primary" />
                  Upload Inspiration / Mood Board
                </FormLabel>
                <FormControl>
                  <Input type="file" className="file:bg-primary/10 file:text-primary file:border-0 file:rounded-md file:px-4 file:py-1 file:mr-4 hover:file:bg-primary/20 cursor-pointer h-12 pt-2.5 font-default" onChange={e => onChange(e.target.value)} {...fieldProps} />
                </FormControl>
                <FormDescription className="text-xs font-default">Accepted formats: PDF, JPG, PNG. Max size 5 MB.</FormDescription>
                <FormMessage />
              </FormItem>} />

          {}
          <FormField control={control} name="additional_info" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-foreground">Floral Vision & Details</FormLabel>
                <FormControl>
                  <textarea className="flex min-h-[120px] w-full border-0 border-b border-border bg-background/30 px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 resize-y font-default" placeholder="Describe your color palette, favorite blooms, and overall atmosphere..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
              <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
              <p className="text-destructive text-sm font-default">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold shadow-md bg-primary text-primary-foreground hover:bg-primary/90 transition-all" size="lg">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-5" />
                Sending Inquiry...
              </> : <>
                <Lock className="mr-2 size-5" />
                Request Your Consultation
              </>}
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1 font-default">
            <Lock className="size-3" /> Your privacy is our priority. Data is handled with care.
          </p>
        </Form>
      </CardContent>
    </Card>;
}